import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { money, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Power, CalendarClock, CalendarX, AlertTriangle, Loader2, Lock, Unlock, ListChecks } from "lucide-react";
import { toast } from "sonner";

export function ClubOpsPanel({ role, onViewPending }) {
  const { settings, reload } = useSettings();
  const sym = settings.currency_symbol || "\u20b9";
  const [dayEnds, setDayEnds] = useState([]);
  const [yearEnds, setYearEnds] = useState([]);
  const [preview, setPreview] = useState(null);
  const [confirmDE, setConfirmDE] = useState(false);
  const [confirmYE, setConfirmYE] = useState(false);
  const [closeDlg, setCloseDlg] = useState(false);
  const [closeReason, setCloseReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const de = await api.get("/club/day-ends"); setDayEnds(de.data);
      if (role === "admin") { const ye = await api.get("/club/year-ends"); setYearEnds(ye.data); }
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const openClub = async () => {
    setBusy(true);
    try { await api.post("/club/open"); await reload(); toast.success("Club opened for service"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const closeClub = async () => {
    setBusy(true);
    try { await api.post("/club/close", { reason: closeReason.trim() }); await reload(); setCloseDlg(false); setCloseReason(""); toast.success("Club closed"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const openDayEnd = async () => {
    try { const { data } = await api.get("/club/day-end/preview"); setPreview(data); setConfirmDE(true); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const runDayEnd = async () => {
    setBusy(true);
    try { const { data } = await api.post("/club/day-end"); await reload();
      toast.success(`Day-End done · Net ${money(data.net, sym)}`); setConfirmDE(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const runYearEnd = async () => {
    setBusy(true);
    try { const { data } = await api.post("/club/year-end"); await reload();
      toast.success(`Year-End complete · New FY ${data.new_financial_year}`); setConfirmYE(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <p className="text-xs uppercase tracking-[0.25em] text-brand-gold mb-1">Club Operations</p>
      <h1 className="font-serif text-3xl sm:text-4xl tracking-tighter text-brand-text mb-6">Open · Day-End · Year-End</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <div className="bg-white border border-brand-line rounded-md p-6" data-testid="club-status-card">
          <div className="flex items-center gap-2 text-brand-muted mb-3">{settings.club_open ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}<span className="text-xs uppercase tracking-wider">Status</span></div>
          <p className={`font-serif text-3xl tracking-tight ${settings.club_open ? "text-brand-ready" : "text-brand-alert"}`}>{settings.club_open ? "Open" : "Closed"}</p>
          <p className="text-sm text-brand-muted mt-2">Business date: {settings.business_date || "—"}</p>
          <p className="text-sm text-brand-muted">Financial year: {settings.financial_year}</p>
        </div>
        <div className="lg:col-span-2 bg-white border border-brand-line rounded-md p-6 flex flex-col justify-center gap-3">
          {/* Live service toggle — open/close at any time */}
          {!settings.club_open ? (
            <Button onClick={openClub} disabled={busy} data-testid="open-club-button" className="bg-brand-ready hover:bg-brand-ready/90 text-white rounded-sm h-12 gap-2"><Power className="w-4 h-4" /> Open Club for Service</Button>
          ) : (
            <Button onClick={() => { setCloseReason(""); setCloseDlg(true); }} disabled={busy} data-testid="close-club-button" className="bg-brand-alert hover:bg-brand-alert/90 text-white rounded-sm h-12 gap-2"><Power className="w-4 h-4" /> Close Club</Button>
          )}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-xs text-brand-muted">Members can order only while the club is <b>Open</b>. Hours: {settings.club_open_time || "—"} – {settings.club_close_time || "—"}.</p>
            <span data-testid="auto-hours-status" className={`text-[11px] font-semibold px-2 py-1 rounded-sm ${settings.auto_hours_enabled ? "bg-brand-ready/15 text-brand-ready" : "bg-brand-line/60 text-brand-muted"}`}>
              Auto-hours {settings.auto_hours_enabled ? "ON" : "OFF"}
            </span>
          </div>
          {!settings.club_open && settings.closed_reason && (
            <p className="text-xs text-brand-alert" data-testid="ops-closed-reason">Closed note: {settings.closed_reason}</p>
          )}

          {/* Financial close operations */}
          <div className="border-t border-brand-line pt-3 mt-1 flex flex-col gap-3">
            <Button onClick={openDayEnd} disabled={busy} data-testid="run-day-end-button" variant="outline" className="rounded-sm h-11 gap-2 border-brand-line"><CalendarClock className="w-4 h-4" /> Run Day-End (Financial Close)</Button>
            {role === "admin" && (
              <Button onClick={() => setConfirmYE(true)} disabled={busy || settings.club_open} data-testid="run-year-end-button" variant="outline" className="rounded-sm h-11 gap-2 border-brand-line"><CalendarX className="w-4 h-4" /> Run Year-End (Financial Rollover)</Button>
            )}
            <p className="text-xs text-brand-muted">Day-End requires zero active/unfulfilled orders.{role === "admin" ? " Close the club before Year-End." : ""}</p>
          </div>
        </div>
      </div>

      <h3 className="text-lg font-semibold mb-3">Day-End History</h3>
      <div className="bg-white border border-brand-line rounded-md overflow-hidden overflow-x-auto mb-8">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent">{["Date", "Invoices", "Sales", "Collected", "Expenses", "Net", "Closed By"].map((h, i) => <TableHead key={i} className="text-xs uppercase tracking-wider">{h}</TableHead>)}</TableRow></TableHeader>
          <TableBody>
            {dayEnds.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-brand-muted py-8">No day-end records yet.</TableCell></TableRow>}
            {dayEnds.map((d) => (
              <TableRow key={d.id} data-testid={`dayend-row-${d.id}`} className="hover:bg-brand-bg/50">
                <TableCell className="font-medium">{d.business_date}</TableCell>
                <TableCell>{d.invoice_count}</TableCell>
                <TableCell>{money(d.sales, sym)}</TableCell>
                <TableCell>{money(d.collected, sym)}</TableCell>
                <TableCell>{money(d.expenses, sym)}</TableCell>
                <TableCell className="font-semibold">{money(d.net, sym)}</TableCell>
                <TableCell className="text-brand-muted">{d.closed_by}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {role === "admin" && (
        <>
          <h3 className="text-lg font-semibold mb-3">Year-End History</h3>
          <div className="bg-white border border-brand-line rounded-md overflow-hidden overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent">{["Financial Year", "Total Sales", "Collected", "Expenses", "Net", "Carry Fwd Due", "Closed By"].map((h, i) => <TableHead key={i} className="text-xs uppercase tracking-wider">{h}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {yearEnds.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-brand-muted py-8">No year-end records yet.</TableCell></TableRow>}
                {yearEnds.map((y) => (
                  <TableRow key={y.id} data-testid={`yearend-row-${y.id}`} className="hover:bg-brand-bg/50">
                    <TableCell className="font-medium">{y.financial_year}</TableCell>
                    <TableCell>{money(y.total_sales, sym)}</TableCell>
                    <TableCell>{money(y.total_collected, sym)}</TableCell>
                    <TableCell>{money(y.total_expenses, sym)}</TableCell>
                    <TableCell className="font-semibold">{money(y.net, sym)}</TableCell>
                    <TableCell>{money(y.carry_forward_outstanding, sym)}</TableCell>
                    <TableCell className="text-brand-muted">{y.closed_by}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {/* Close club with optional reason */}
      <Dialog open={closeDlg} onOpenChange={setCloseDlg}>
        <DialogContent className="bg-brand-bg max-w-md" data-testid="close-club-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl tracking-tight text-left">Close Club</DialogTitle><DialogDescription className="text-left">Ordering will be paused. Add an optional note members will see on the closed banner.</DialogDescription></DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold">Reason / note (optional)</Label>
            <Input value={closeReason} onChange={(e) => setCloseReason(e.target.value)} placeholder="e.g. Kitchen break, back at 4 PM" data-testid="close-reason-input" className="border-brand-line" />
          </div>
          <DialogFooter>
            <Button onClick={closeClub} disabled={busy} data-testid="confirm-close-club" className="bg-brand-alert hover:bg-brand-alert/90 text-white rounded-sm w-full">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Close Club"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Day-End confirm */}
      <Dialog open={confirmDE} onOpenChange={setConfirmDE}>
        <DialogContent className="bg-brand-bg max-w-md" data-testid="dayend-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl tracking-tight text-left">Confirm Day-End</DialogTitle><DialogDescription className="sr-only">Review and close the business day</DialogDescription></DialogHeader>
          {preview && <div className="space-y-2 text-sm">
            <p className="text-brand-muted">Closing business date <b className="text-brand-text">{preview.business_date}</b></p>
            {preview.active_orders > 0 ? (
              <div className="flex items-center gap-2 text-brand-alert bg-brand-alert/10 rounded-sm p-3" data-testid="dayend-blocked"><AlertTriangle className="w-4 h-4 shrink-0" /> Day-End cannot be performed. One or more orders are pending completion.</div>
            ) : (
              <div className="grid grid-cols-2 gap-2 pt-2">
                <Row l="Invoices" v={preview.invoices} /><Row l="Sales" v={money(preview.sales, sym)} />
                <Row l="Collected" v={money(preview.collected, sym)} /><Row l="Expenses" v={money(preview.expenses, sym)} />
              </div>
            )}
          </div>}
          <DialogFooter>
            {preview && !preview.can_close ? (
              <Button onClick={() => { setConfirmDE(false); onViewPending && onViewPending(); }} data-testid="view-pending-orders" className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm w-full gap-2"><ListChecks className="w-4 h-4" /> View Pending Orders</Button>
            ) : (
              <Button onClick={runDayEnd} disabled={busy} data-testid="confirm-day-end" className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm w-full">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Close the Day"}</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Year-End confirm */}
      <Dialog open={confirmYE} onOpenChange={setConfirmYE}>
        <DialogContent className="bg-brand-bg max-w-md" data-testid="yearend-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl tracking-tight text-left">Confirm Year-End</DialogTitle><DialogDescription className="sr-only">Finalise and roll over the financial year</DialogDescription></DialogHeader>
          <p className="text-sm text-brand-muted">This will finalise financial year <b className="text-brand-text">{settings.financial_year}</b>, archive the summary, and roll over to the next year. This cannot be undone.</p>
          <DialogFooter>
            <Button onClick={runYearEnd} disabled={busy} data-testid="confirm-year-end" className="bg-brand-alert hover:bg-brand-alert/90 text-white rounded-sm w-full">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Run Year-End"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Row({ l, v }) {
  return <div className="flex justify-between border border-brand-line rounded-sm px-3 py-2"><span className="text-brand-muted">{l}</span><span className="font-semibold">{v}</span></div>;
}
